import React, { useState, useEffect } from 'react';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import {
  TrashIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  UserIcon,
  KeyIcon,
  LockClosedIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import {
  useAddSnowflakeAccountMutation,
  useAddSnowflakeAccountPKeyMutation,
  useValidateSnowflakeAccountMutation,
  useGetOauthIntegrationsQuery,
  useGetSnowflakeAccountsQuery,
  useLazyGetSnowflakeWarehousesQuery,
  useLazyGetSnowflakeRolesQuery,
} from 'app/createApi';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  selectSnowflakeAccount,
  setSelectedDatabase,
  selectSnowflakeAuthType,
  setSnowflakeAuthType,
  setSnowflakeAccount,
  setSnowflakeAccountLabel,
  setSnowflakeAccountConnected,
  setSnowflakeRole,
  setSnowflakeWarehouse,
  setPersistedAccountValidationFailed,
  selectPersistedAccountValidationFailed,
} from 'app/snowflakeSlice';
import { clearSelectedObjects } from '../features/data-product-builder/reducers/builderSlice';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { classNames } from 'utils/styleUtils';
import AccountFormButton from './AccountFormButton';
import SnowflakeOauthAccountDialog from './SnowflakeOauthAccountDialog';

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: '#4b5563',
  color: 'white',
  fontSize: 12,
  zIndex: 100,
  maxWidth: 300,
};

const OAuthAccountLabel = ({ account }: { account: string }): JSX.Element => {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<string>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ scroll: true });

  return (
    <dd
      ref={containerRef}
      className="text-gray-700 truncate cursor-default"
      onMouseLeave={() => {
        hideTooltip();
      }}
      onMouseMove={(event) => {
        const coords = localPoint(event);
        showTooltip({ tooltipData: account, tooltipTop: coords?.y, tooltipLeft: coords?.x });
      }}
    >
      {account}
      {tooltipOpen && tooltipData !== undefined && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="text-sm">{tooltipData}</div>
        </TooltipInPortal>
      )}
    </dd>
  );
};

const InfoSection = (): JSX.Element => (
  <Disclosure as="div" defaultOpen={false} className="w-full mx-[auto] rounded-md bg-purple-50 overflow-hidden">
    {({ open }) => (
      <>
        <DisclosureButton className="w-full p-2 flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-purple-400" aria-hidden="true" />
          </div>
          <div className="ml-3 grow text-left flex gap-x-1">
            <p className="text-sm text-purple-700 font-semibold">Heads up!</p>
            <p className="text-sm text-purple-700">We suggest using a Service User</p>
          </div>
          <ChevronRightIcon className={classNames('h-5 w-5 text-purple-500', open ? 'rotate-90' : '')} />
        </DisclosureButton>
        <DisclosurePanel
          transition
          className="px-4 pb-4 origin-top transition duration-200 ease-out data-closed:-translate-y-6 data-closed:opacity-0"
        >
          <div className="ml-6 flex-1 space-y-2">
            <p className="text-sm text-purple-700">
              We highly recommend using a Service User for this flow. To create a Service User, please follow the
              instructions provided by Snowflake:{' '}
              <a
                className="hover:underline font-[600] flex"
                href="https://docs.snowflake.com/en/sql-reference/sql/create-user"
                target="_blank"
                rel="noreferrer"
              >
                <span>Create User Documentation</span>
                <ArrowTopRightOnSquareIcon className="size-3 text-purple-700" />
              </a>
            </p>
            <p className="text-sm text-purple-700">
              You can use a personal user with Multi-Factor Authentication (MFA), but you might get multiple approval
              notifications based on your Snowflake instance&apos;s token caching settings.
            </p>
          </div>
        </DisclosurePanel>
      </>
    )}
  </Disclosure>
);

const AccountFormatInfoSection = (): JSX.Element => (
  <Disclosure as="div" defaultOpen={false} className="w-full mx-[auto] rounded-md bg-blue-50 overflow-hidden">
    {({ open }) => (
      <>
        <DisclosureButton className="w-full p-2 flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
          </div>
          <div className="ml-3 grow text-left flex gap-x-1">
            <p className="text-sm text-blue-700 font-semibold"></p>
            <p className="text-sm text-blue-700">How to get the account identifier?</p>
          </div>
          <ChevronRightIcon className={classNames('h-5 w-5 text-blue-500', open ? 'rotate-90' : '')} />
        </DisclosureButton>
        <DisclosurePanel
          transition
          className="px-4 pb-4 origin-top transition duration-200 ease-out data-closed:-translate-y-6 data-closed:opacity-0"
        >
          <div className="ml-6 flex-1 space-y-2">
            <p className="text-sm text-blue-700">
              In the <span className="font-semibold">Account</span> field, you need to provide your{' '}
              <span className="font-semibold">Account Identifier</span> from Snowflake.
              <ol className="list-decimal list-outside text-sm text-blue-700 space-y-2 pl-5">
                <li className="pl-1">
                  Log in to your Snowflake account and open the account selector (located in the lower-left corner).
                </li>
                <li className="pl-1">Hover over the account to expand a menu on the right.</li>
                <li className="pl-1">
                  Select <span className="font-semibold">View account details</span> for the desired account (if you
                  have multiple accounts, you may need to scroll to find the correct one).
                </li>
                <li className="pl-1">
                  The Account Details dialog will display information about the account, including the{' '}
                  <span className="font-semibold">Account Identifier</span>.
                </li>
              </ol>
            </p>
            <p className="text-sm text-blue-700">
              Or alternatively, run the following query in Snowflake:
              <br />
              <code className="block w-full bg-blue-50 border border-blue-200 px-3 py-2 rounded-md font-mono text-sm text-blue-800 shadow-sm overflow-x-auto my-2">
                {`SELECT CURRENT_ORGANIZATION_NAME() || '-' || CURRENT_ACCOUNT_NAME();`}
              </code>
            </p>
          </div>
        </DisclosurePanel>
      </>
    )}
  </Disclosure>
);

export interface IOauthIntegration {
  account: string;
  name?: string;
  client_id: string;
  client_secret: string;
  id: number;
  is_editable: boolean;
  token_url: string;
  warehouse: string;
  role: string;
  is_shared_with_group: boolean;
}

interface SnowflakeAccountFormProps {
  snowflakeAccount: string;
  setSnowflakeAccount: (account: string) => void;
  snowflakeUsername: string;
  setSnowflakeUsername: (username: string) => void;
  snowflakeWarehouse: string;
  setSnowflakeWarehouse: (warehouse: string) => void;
  snowflakeRole: string;
  setSnowflakeRole: (role: string) => void;
  snowflakeAccountConnected: boolean;
  setSnowflakeAccountConnected: (connected: boolean) => void;
  snowflakePrivateKey: string;
  setSnowflakePrivateKey: (privateKey: string) => void;
  snowflakePassphrase: string;
  setSnowflakePassphrase: (passphrase: string) => void;
  setFailed?: (failed: boolean) => void;
  setErrorMessage?: (message: string) => void;
}

export default function SnowflakeAccountForm(props: SnowflakeAccountFormProps): JSX.Element {
  const dispatch = useAppDispatch();
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const snowflakeAuthType = useAppSelector(selectSnowflakeAuthType);
  const persistedAccountValidationFailed = useAppSelector(selectPersistedAccountValidationFailed);

  // Local state variables for form fields
  const [localAccount, setLocalAccount] = useState<string>('');
  const [localUsername, setLocalUsername] = useState<string>('');
  const [localWarehouse, setLocalWarehouse] = useState<string>('');
  const [localRole, setLocalRole] = useState<string>('');
  const [localPrivateKey, setLocalPrivateKey] = useState<string>('');
  const [localPassphrase, setLocalPassphrase] = useState<string>('');
  const [localId, setLocalId] = useState<string>('');
  console.log('TOp account ID:', localId);
  const [newAccountSuccessfullyAdded, setNewAccountSuccessfullyAdded] = useState<boolean>(false);
  // State for warehouse and role dropdowns
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState<boolean>(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState<boolean>(false);
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [warehousesFetched, setWarehousesFetched] = useState<boolean>(false);
  // Removed unused state

  // nosec: This is a React controlled-input initializer (empty string default), not a hardcoded credential
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Couldn't connect to Snowflake");
  const [persistedAccountValidating, setPersistedAccountValidating] = useState<boolean>(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(false);
  const [accountToDelete, setAccountToDelete] = useState<string>('');

  const [addSnowflakeAccount] = useAddSnowflakeAccountMutation();
  const [addSnowflakeAccountPKey] = useAddSnowflakeAccountPKeyMutation();
  const [validateSnowflakeAccount] = useValidateSnowflakeAccountMutation();

  const { data: snowflakeAccounts } = useGetSnowflakeAccountsQuery({});
  const [hasPersistedSnowflakeAccounts, setHasPersistedSnowflakeAccounts] = useState<boolean>(false);
  const { data: oauthIntegrations } = useGetOauthIntegrationsQuery([]);

  // API hooks for fetching warehouses and roles
  const [getWarehouses, { isLoading: isLoadingWarehouses }] = useLazyGetSnowflakeWarehousesQuery();
  const [getRoles, { isLoading: isLoadingRoles }] = useLazyGetSnowflakeRolesQuery();

  const [openAuthenticateDialog, setOpenAuthenticateDialog] = useState<boolean>(false);
  const [integrationIndex, setIntegrationIndex] = useState<number>(-999);

  const [tabs, setTabs] = useState(['Username-Password', 'Key-Pair']);

  useEffect(() => {
    const tmpTabs = ['Username-Password', 'Key-Pair'];
    if (oauthIntegrations !== undefined && oauthIntegrations.length > 0) {
      tmpTabs.push('OAuth');
    }
    if (hasPersistedSnowflakeAccounts) {
      tmpTabs.unshift('Use Existing');
      dispatch(setSnowflakeAuthType('persisted'));
    }
    setTabs(tmpTabs);
  }, [hasPersistedSnowflakeAccounts, oauthIntegrations]);

  useEffect(() => {
    if (snowflakeAccounts !== undefined && Object.keys(snowflakeAccounts).length > 0) {
      setHasPersistedSnowflakeAccounts(true);
    }
  }, [snowflakeAccounts]);

  useEffect(() => {
    if (!hasPersistedSnowflakeAccounts && snowflakeAuthType === 'persisted') {
      dispatch(setSnowflakeAuthType('password'));
    }
  }, [hasPersistedSnowflakeAccounts, snowflakeAuthType]);

  useEffect(() => {
    if (hasPersistedSnowflakeAccounts && Object.keys(snowflakeAccounts).length === 1) {
      const onlyKey = Object.keys(snowflakeAccounts)[0];
      dispatch(setSnowflakeAccountLabel(snowflakeAccounts[onlyKey].account));
      dispatch(setSnowflakeAccount(onlyKey));
      dispatch(setSnowflakeRole(snowflakeAccounts[onlyKey].role));
      dispatch(setSnowflakeWarehouse(snowflakeAccounts[onlyKey].warehouse));
      dispatch(setSnowflakeAccountConnected(true));
      setLocalId(onlyKey); // Set the local ID for the only account
    }
  }, [hasPersistedSnowflakeAccounts, snowflakeAccounts]);

  useEffect(() => {
    if (
      snowflakeAccounts !== undefined &&
      Object.keys(snowflakeAccounts).length > 0 &&
      snowflakeAccount in snowflakeAccounts
    ) {
      setSnowflakeAccountLabel(snowflakeAccounts[snowflakeAccount]?.account);
    }
  }, [snowflakeAccounts, snowflakeAccount]);

  // Function to fetch warehouses and roles using the localId
  const fetchWarehouses = (): void => {
    if (localId !== '' && !warehousesFetched) {
      getWarehouses({ account_id: localId })
        .unwrap()
        .then((data) => {
          setWarehouses(data);
          setWarehousesFetched(true);
          // After getting warehouses, also fetch roles
          getRoles({ account_id: localId })
            .unwrap()
            .then((rolesData) => {
              setRoles(rolesData);
            })
            .catch((err) => {
              console.error('Error fetching roles:', err);
            });
        })
        .catch((err) => {
          console.error('Error fetching warehouses:', err);
        });
    }
  };

  const onSelectPersistedAccount = (account: string): void => {
    if (snowflakeAccount === account) {
      dispatch(setSnowflakeAccount(''));
      dispatch(setSnowflakeRole(''));
      dispatch(setSnowflakeWarehouse(''));
      dispatch(setSnowflakeAccountConnected(false));
      dispatch(setPersistedAccountValidationFailed(false));
      setLocalId('');
    } else {
      dispatch(setSnowflakeAccountLabel(snowflakeAccounts[account as any].account));
      dispatch(setSnowflakeAccount(account));
      dispatch(setSnowflakeRole(snowflakeAccounts[account as any].role));
      dispatch(setSnowflakeWarehouse(snowflakeAccounts[account as any].warehouse));

      dispatch(setSnowflakeAccountConnected(false));
      if (props.setFailed !== undefined) {
        props.setFailed(false);
      }
      if (props.setErrorMessage !== undefined) {
        props.setErrorMessage('');
      }
      dispatch(setPersistedAccountValidationFailed(false));
      setPersistedAccountValidating(true);
      validateSnowflakeAccount({
        account,
      })
        .unwrap()
        .then((res: any) => {
          dispatch(setSnowflakeAccountConnected(true));

          // Fetch warehouses and roles for the selected account
          getWarehouses({ account_id: account })
            .unwrap()
            .then((data) => {
              setWarehouses(data);
              setWarehousesFetched(true);
            })
            .catch((err) => {
              console.error('Error fetching warehouses:', err);
            });

          getRoles({ account_id: account })
            .unwrap()
            .then((data) => {
              setRoles(data);
              // setRolesFetched(true);
            })
            .catch((err) => {
              console.error('Error fetching roles:', err);
            });
        })
        .catch((err) => {
          console.log(err);
          dispatch(setPersistedAccountValidationFailed(true));
          if (props.setFailed !== undefined) {
            props.setFailed(true);
          }
          if (props.setErrorMessage !== undefined) {
            props.setErrorMessage('The details of the persisted account are no longer valid');
          }
        })
        .finally(() => {
          setPersistedAccountValidating(false);
        });
    }
  };

  const handleConnect = (): void => {
    // Validate warehouse field
    // if (localWarehouse === '') {
    //   setFailed(true);
    //   setErrorMessage('Warehouse is required. Please select or enter a warehouse.');
    //   return;
    // }

    // Update props with local state values when the button is clicked
    props.setSnowflakeAccount(localAccount);
    props.setSnowflakeUsername(localUsername);
    props.setSnowflakeWarehouse(localWarehouse);
    props.setSnowflakeRole(localRole);
    props.setSnowflakePrivateKey(localPrivateKey);
    props.setSnowflakePassphrase(localPassphrase);

    props.setSnowflakeAccountConnected(false);
    setNewAccountSuccessfullyAdded(false);
    setFailed(false);
    setLoading(true);
    if (snowflakeAuthType === 'password') {
      addSnowflakeAccount({
        account: localAccount,
        username: localUsername,
        password,
        warehouse: localWarehouse,
        role: localRole,
      })
        .unwrap()
        .then((res: any) => {
          console.log(res);
          props.setSnowflakeAccountConnected(true);
          setNewAccountSuccessfullyAdded(true);
          setLocalId(String(res));
          console.log('New account ID:', res);
          dispatch(setSnowflakeAccount(String(res)));
          setFailed(false);
          dispatch(setSelectedDatabase(''));
          dispatch(clearSelectedObjects());
        })
        .catch((err) => {
          console.log(err);
          props.setSnowflakeAccountConnected(false);
          setNewAccountSuccessfullyAdded(false);
          if (err.data !== undefined) setErrorMessage(err.data);
          else setErrorMessage("Couldn't connect to Snowflake");
          setFailed(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      addSnowflakeAccountPKey({
        account: localAccount,
        username: localUsername,
        warehouse: localWarehouse,
        role: localRole,
        privateKey: localPrivateKey,
        privateKeyPwd: localPassphrase,
      })
        .unwrap()
        .then((res: any) => {
          console.log(res);
          props.setSnowflakeAccountConnected(true);
          setNewAccountSuccessfullyAdded(true);
          dispatch(setSnowflakeAccount(String(res)));
          setLocalId(String(res));
          setFailed(false);
          dispatch(setSelectedDatabase(''));
          dispatch(clearSelectedObjects());
        })
        .catch((err) => {
          console.log(err);
          props.setSnowflakeAccountConnected(false);
          setNewAccountSuccessfullyAdded(false);
          if (err.data !== undefined) setErrorMessage(err.data);
          else setErrorMessage("Couldn't connect to Snowflake");
          setFailed(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div className="w-[35rem] max-w-full mx-[auto]">
      <div className="">
        <div className="border-b border-gray-200 mb-6">
          <nav aria-label="Tabs" className="-mb-px flex justify-between">
            {tabs.map((tab) => (
              <div
                key={tab}
                onClick={() =>
                  dispatch(
                    setSnowflakeAuthType(
                      tab === 'Username-Password'
                        ? 'password'
                        : tab === 'Key-Pair'
                        ? 'key-pair'
                        : tab === 'OAuth'
                        ? 'oauth'
                        : 'persisted',
                    ),
                  )
                }
                className={classNames(
                  (tab === 'Username-Password' && snowflakeAuthType === 'password') ||
                    (tab === 'Key-Pair' && snowflakeAuthType === 'key-pair') ||
                    (tab === 'Use Existing' && snowflakeAuthType === 'persisted') ||
                    (tab === 'OAuth' && snowflakeAuthType === 'oauth')
                    ? 'border-dataops-primary text-dataops-primary'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'flex-1 whitespace-nowrap border-b-2 px-1 py-2 text-center text-sm font-medium cursor-pointer',
                )}
              >
                {tab}
              </div>
            ))}
          </nav>
        </div>
      </div>
      {snowflakeAuthType === 'password' && (
        <div className="space-y-4 flex flex-col items-center">
          <div className="w-full space-y-1">
            {process.env.REACT_APP_ENV !== 'az' && <InfoSection />}
            <AccountFormatInfoSection />
          </div>
          <div className="rounded-md shadow-sm w-fit">
            <div className="flex rounded-md shadow-sm">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Account
              </span>
              <input
                value={localAccount}
                name="account"
                id="account-field"
                onChange={(e) => setLocalAccount(e.target.value)}
                placeholder="zdxupxd-training"
                required
                className="relative w-[14rem] block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div className="flex rounded-md shadow-sm">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Username
              </span>
              <input
                value={localUsername}
                name="username"
                id="username-field"
                onChange={(e) => setLocalUsername(e.target.value)}
                placeholder="dataops_obs"
                required
                className="relative w-[14rem] block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div className="flex rounded-md shadow-sm">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Password
              </span>
              <input
                type="password"
                name="password"
                id="password-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
                className="relative w-[14rem] block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div className="flex rounded-md shadow-sm relative">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Warehouse
              </span>
              <div className="relative w-[14rem]">
                <input
                  value={localWarehouse}
                  name="warehouse"
                  id="warehouse-field"
                  onChange={(e) => setLocalWarehouse(e.target.value)}
                  onFocus={() => {
                    if (localId !== '') {
                      fetchWarehouses();
                      setWarehouseDropdownOpen(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setWarehouseDropdownOpen(false), 200)}
                  placeholder="DATAOPS_OBS_WH"
                  className="relative w-full block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm pr-8"
                />
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                </div>

                {warehouseDropdownOpen && warehouses.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {warehouses.map((warehouse) => (
                      <div
                        key={warehouse}
                        className={classNames(
                          'cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100',
                          localWarehouse === warehouse ? 'bg-cyan-100' : '',
                        )}
                        onClick={() => {
                          setLocalWarehouse(warehouse);
                          setWarehouseDropdownOpen(false);
                        }}
                      >
                        <div className="flex items-center">
                          <span className="font-normal block truncate">{warehouse}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isLoadingWarehouses && (
                  <div className="absolute right-10 top-2">
                    <div className="animate-spin h-4 w-4 border-2 border-cyan-500 rounded-full border-t-transparent"></div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex rounded-md shadow-sm relative">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Role
              </span>
              <div className="relative w-[14rem]">
                <input
                  value={localRole}
                  name="role"
                  id="role-field"
                  onChange={(e) => setLocalRole(e.target.value)}
                  onFocus={() => {
                    if (localId !== '') {
                      fetchWarehouses(); // This will also fetch roles
                      setRoleDropdownOpen(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setRoleDropdownOpen(false), 200)}
                  placeholder="DATAOPS_OBS_VIEWER"
                  className="relative w-full block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm pr-8"
                />
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                </div>

                {roleDropdownOpen && roles.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {roles.map((role) => (
                      <div
                        key={role}
                        className={classNames(
                          'cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100',
                          localRole === role ? 'bg-cyan-100' : '',
                        )}
                        onClick={() => {
                          setLocalRole(role);
                          setRoleDropdownOpen(false);
                        }}
                      >
                        <div className="flex items-center">
                          <span className="font-normal block truncate">{role}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isLoadingRoles && (
                  <div className="absolute right-10 top-2">
                    <div className="animate-spin h-4 w-4 border-2 border-cyan-500 rounded-full border-t-transparent"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {loading && !failed && (
            <div className="min-h-6 flex items-center justify-center">
              <div
                className="animate-spin inline-block w-6 h-6 border-[2px] border-current border-t-transparent text-dataops-primary rounded-full"
                role="status"
                aria-label="loading"
              >
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => handleConnect()}
            className={classNames(
              'w-[22rem] rounded-md py-1 px-3 shadow-sm',
              newAccountSuccessfullyAdded
                ? 'bg-white text-gray-900 hover:bg-gray-200 ring-1 ring-gray-300'
                : 'bg-dataops-primary text-white hover:bg-dataops-primary-hover disabled:bg-gray-400',
            )}
          >
            Connect
          </button>
          <div className="w-[22rem]">
            {failed && (
              <div className="min-h-6 w-full flex items-center justify-center">
                <div className="text-sm w-full rounded bg-red-50 border-2 border-red-500 flex items-center justify-center">
                  <a className="w-full text-center font-medium text-red-600 hover:text-red-500 break-words p-1">
                    {errorMessage}
                  </a>
                </div>
              </div>
            )}
            {newAccountSuccessfullyAdded && (
              <div className="min-h-6 w-full flex items-center justify-center">
                <div className="text-sm w-full rounded bg-green-50 border-2 border-green-500 flex items-center justify-center">
                  <a className="font-medium text-green-600 hover:text-green-500">Account added</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {snowflakeAuthType === 'key-pair' && (
        <div className="space-y-4 flex flex-col items-center">
          <AccountFormatInfoSection />
          <div className="rounded-md shadow-sm w-fit">
            <div className="flex rounded-md shadow-sm">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Account
              </span>
              <input
                value={localAccount}
                name="account"
                id="account-field"
                onChange={(e) => setLocalAccount(e.target.value)}
                placeholder="zdxupxd-training"
                required
                className="relative w-[14rem] block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div className="flex rounded-md shadow-sm">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Username
              </span>
              <input
                value={localUsername}
                name="username"
                id="username-field"
                onChange={(e) => setLocalUsername(e.target.value)}
                placeholder="dataops_obs"
                required
                className="relative w-[14rem] block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div className="flex rounded-md shadow-sm">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Private Key
              </span>
              <textarea
                rows={1}
                name="privatekey"
                id="privatekey-field"
                value={localPrivateKey}
                onChange={(e) => setLocalPrivateKey(e.target.value)}
                placeholder="Paste your private key here"
                required
                className={classNames(
                  localPrivateKey === '' ? '' : 'blurred-text',
                  'relative w-[14rem] block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm',
                )}
              />
            </div>
            <div className="flex rounded-md shadow-sm">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Passphrase
              </span>
              <input
                type="password"
                name="passphrase"
                id="passphrase-field"
                value={localPassphrase}
                onChange={(e) => setLocalPassphrase(e.target.value)}
                placeholder="Passphrase for private key"
                className="relative w-[14rem] block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div className="flex rounded-md shadow-sm relative">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Warehouse
              </span>
              <div className="relative w-[14rem]">
                <input
                  value={localWarehouse}
                  name="warehouse"
                  id="warehouse-field"
                  onChange={(e) => setLocalWarehouse(e.target.value)}
                  onFocus={() => {
                    if (localId !== '') {
                      fetchWarehouses();
                      setWarehouseDropdownOpen(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setWarehouseDropdownOpen(false), 200)}
                  placeholder="DATAOPS_OBS_WH"
                  className="relative w-full block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm pr-8"
                />
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                </div>

                {warehouseDropdownOpen && warehouses.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {warehouses.map((warehouse) => (
                      <div
                        key={warehouse}
                        className={classNames(
                          'cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100',
                          localWarehouse === warehouse ? 'bg-cyan-100' : '',
                        )}
                        onClick={() => {
                          setLocalWarehouse(warehouse);
                          setWarehouseDropdownOpen(false);
                        }}
                      >
                        <div className="flex items-center">
                          <span className="font-normal block truncate">{warehouse}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex rounded-md shadow-sm relative">
              <span className="w-[8rem] inline-flex items-center border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-500 sm:text-sm">
                Role
              </span>
              <div className="relative w-[14rem]">
                <input
                  value={localRole}
                  name="role"
                  id="role-field"
                  onChange={(e) => setLocalRole(e.target.value)}
                  onFocus={() => {
                    if (localId !== '') {
                      fetchWarehouses(); // This will also fetch roles
                      setRoleDropdownOpen(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setRoleDropdownOpen(false), 200)}
                  placeholder="DATAOPS_OBS_VIEWER"
                  className="relative w-full block appearance-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm pr-8"
                />
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                </div>

                {roleDropdownOpen && roles.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {roles.map((role) => (
                      <div
                        key={role}
                        className={classNames(
                          'cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100',
                          localRole === role ? 'bg-cyan-100' : '',
                        )}
                        onClick={() => {
                          setLocalRole(role);
                          setRoleDropdownOpen(false);
                        }}
                      >
                        <div className="flex items-center">
                          <span className="font-normal block truncate">{role}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {loading && !failed && (
            <div className="min-h-6 flex items-center justify-center">
              <div
                className="animate-spin inline-block w-6 h-6 border-[2px] border-current border-t-transparent text-dataops-primary rounded-full"
                role="status"
                aria-label="loading"
              >
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => handleConnect()}
            className={classNames(
              'w-[22rem] rounded-md py-1 px-3 shadow-sm',
              newAccountSuccessfullyAdded
                ? 'bg-white text-gray-900 hover:bg-gray-200 ring-1 ring-gray-300'
                : 'bg-dataops-primary text-white hover:bg-dataops-primary-hover disabled:bg-gray-400',
            )}
          >
            Connect
          </button>
          <div className="w-[22rem]">
            {failed && (
              <div className="min-h-6 w-full flex items-center justify-center">
                <div className="text-sm w-full rounded bg-red-50 border-2 border-red-500 flex items-center justify-center">
                  <a className="w-full text-center font-medium text-red-600 hover:text-red-500 break-words p-1">
                    {errorMessage}
                  </a>
                </div>
              </div>
            )}
            {newAccountSuccessfullyAdded && (
              <div className="min-h-6 w-full flex items-center justify-center">
                <div className="text-sm w-full rounded bg-green-50 border-2 border-green-500 flex items-center justify-center">
                  <a className="font-medium text-green-600 hover:text-green-500">Account added</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {snowflakeAuthType === 'persisted' && (
        <>
          <p className="text-center text-sm text-gray-600 mb-4">
            If you connected a Snowflake account previously then you can just select it from the listing below
          </p>
          <ul className="w-full max-w-3xl mx-[auto] grid grid-cols-2 gap-4">
            {snowflakeAccounts !== undefined &&
              Object.keys(snowflakeAccounts ?? []).map((account: string) => (
                <li
                  key={account}
                  className={classNames(
                    'w-full overflow-hidden rounded-md border cursor-pointer shadow-sm',
                    snowflakeAccount === account
                      ? persistedAccountValidationFailed
                        ? 'border-red-600'
                        : 'border-dataops-primary'
                      : 'border-gray-200',
                  )}
                  onClick={() => onSelectPersistedAccount(account)}
                >
                  <div className="w-full px-2 flex items-center border-b border-gray-900/5 bg-gray-50 py-1">
                    <div className="text-gray-700 whitespace-nowrap overflow-hidden truncate">
                      {snowflakeAccounts[account as any].account}
                    </div>
                    <TrashIcon
                      className="h-5 w-5 min-w-5 text-gray-400 hover:text-red-500 ml-[auto]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAccountToDelete(account);
                        setOpenDeleteConfirmation(true);
                      }}
                    />
                  </div>
                  <div className="px-2 py-1">
                    <div className="flex items-center py-1">
                      {snowflakeAccount === account && persistedAccountValidating && (
                        <div
                          className="animate-spin inline-block h-3 w-3 min-w-3 border-[1.5px] border-current border-t-transparent text-dataops-primary rounded-full mr-2"
                          role="status"
                          aria-label="loading"
                        >
                          <span className="sr-only">Loading...</span>
                        </div>
                      )}
                      {snowflakeAccount === account && !persistedAccountValidating && (
                        <div
                          className={classNames(
                            'h-3 w-3 min-w-3 rounded-full mr-2',
                            persistedAccountValidationFailed ? 'bg-red-600' : 'bg-dataops-primary',
                          )}
                        ></div>
                      )}
                      {snowflakeAccount !== account && (
                        <div className="h-3 w-3 min-w-3 rounded-full border border-gray-300 mr-2"></div>
                      )}
                      <UserIcon className="h-4 w-4 min-w-4 text-gray-500 mr-1" />
                      <div className="text-sm text-gray-700 flex whitespace-nowrap overflow-hidden">
                        <span className="text-gray-500">Username:</span>&nbsp;
                        <span className="truncate">{snowflakeAccounts[account as any].username}</span>
                      </div>
                    </div>
                    <div className="flex items-center py-1">
                      <div className="h-3 w-3 opacity-0 mr-2"></div>
                      <KeyIcon className="h-4 w-4 min-w-4 text-gray-500 mr-1" />
                      <div className="text-sm text-gray-700 flex whitespace-nowrap overflow-hidden">
                        <span className="text-gray-500">Role:</span>&nbsp;
                        <span className="truncate">{snowflakeAccounts[account as any].role}</span>
                      </div>
                    </div>
                    <div className="flex items-center py-1">
                      <div className="h-3 w-3 opacity-0 mr-2"></div>
                      <LockClosedIcon className="h-4 w-4 min-w-4 text-gray-500 mr-1" />
                      <div className="text-sm text-gray-700 flex whitespace-nowrap overflow-hidden">
                        <span className="text-gray-500">Type:</span>&nbsp;
                        <span className="truncate">{snowflakeAccounts[account as any].type}</span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
          {snowflakeAccounts !== undefined && Object.keys(snowflakeAccounts).length === 0 && (
            <div className="text-sm text-gray-500">No persisted accounts</div>
          )}
          <DeleteConfirmationDialog
            open={openDeleteConfirmation}
            setOpen={setOpenDeleteConfirmation}
            account={accountToDelete}
          />
        </>
      )}
      {snowflakeAuthType === 'oauth' && (
        <>
          <ul className="w-[22rem] mx-[auto] flex flex-col items-center space-y-2">
            {oauthIntegrations?.map((integration: IOauthIntegration, index: number) => (
              <li
                key={integration.account}
                className={classNames(
                  'w-4/5 overflow-hidden rounded-md border cursor-pointer shadow-sm',
                  snowflakeAccount === integration.account ? 'border-gray-400' : 'border-gray-200',
                )}
              >
                <div className="w-full px-2 flex items-center border-b border-gray-900/5 bg-gray-50 py-1">
                  <div className="ml-2 text-gray-700">{integration.name ?? integration.account}</div>
                </div>
                <dl className="divide-y divide-gray-100 text-sm max-w-full px-2">
                  {integration.name != null && integration.name !== '' && (
                    <div className="flex gap-x-4 py-1">
                      <dt className="min-w-[5rem] text-gray-500">Account</dt>
                      <OAuthAccountLabel account={integration.account} />
                    </div>
                  )}
                  <div className="flex gap-x-4 py-1">
                    <dt className="min-w-[5rem] text-gray-500">Role</dt>
                    <dd className="text-gray-700 truncate">{integration.role}</dd>
                  </div>
                  <div className="flex gap-x-4 py-1">
                    <dt className="min-w-[5rem] text-gray-500">Warehouse</dt>
                    <dd className="text-gray-700 truncate">{integration.warehouse}</dd>
                  </div>
                  <div className="flex gap-x-4 py-1">
                    <AccountFormButton
                      label="Create account"
                      onClick={() => {
                        setOpenAuthenticateDialog(true);
                        setIntegrationIndex(index);
                      }}
                    />
                  </div>
                </dl>
              </li>
            ))}
          </ul>
          {integrationIndex >= 0 && (
            <SnowflakeOauthAccountDialog
              integration={oauthIntegrations[integrationIndex]}
              dialogOpen={openAuthenticateDialog}
              setDialogOpen={setOpenAuthenticateDialog}
            />
          )}
        </>
      )}
    </div>
  );
}
